/**
 * File upload component with preview
 */
/* eslint-disable react/jsx-no-bind */
/* eslint-disable no-console */

import { useState } from 'preact/hooks';
import { useForm } from '@ox/visits-ui/context/FormContext';
import { useFileUpload } from '@ox/visits-ui/hooks/useFileUpload';
import type { MediaFile } from '@ox/ox-brokerage-client';
import '@ox/visits-ui/components/shared/FileUpload.scss';

interface FileUploadProps {
  label: string;
  mediaType: string;
  sectionNumber: number;
  minFiles?: number;
  maxFiles?: number;
  accept?: string;
  required?: boolean;
  helpText?: string;
}

export function FileUpload({
  label,
  mediaType,
  sectionNumber,
  minFiles = 1,
  maxFiles = 1,
  accept = 'image/*',
  required = false,
  helpText,
}: FileUploadProps) {
  const { errors, viewMode, uploadMedia, deleteMedia, getMediaByType } = useForm();
  const { uploadFiles, isUploading, uploadProgress } = useFileUpload(uploadMedia);
  const [error, setError] = useState<string | null>(null);

  const existingMedia = getMediaByType(sectionNumber, mediaType);

  const handleFileSelect = async (e: Event) => {
    const input = e.target as HTMLInputElement;
    const files = Array.from(input.files || []);

    if (files.length === 0) {
      return;
    }

    if (existingMedia.length + files.length > maxFiles) {
      setError(`Maximum ${maxFiles} files allowed`);
      return;
    }

    try {
      setError(null);
      await uploadFiles(files, sectionNumber, mediaType);
      input.value = '';
    } catch (err: unknown) {
      setError((err as Error).message || 'Upload failed');
    }
  };

  const handleDelete = async (mediaId: string) => {
    try {
      await deleteMedia(mediaId);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleMediaClick = (url: string) => {
    window.open(url, '_blank');
  };

  const handleMediaKeyDown = (e: KeyboardEvent, url: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      window.open(url, '_blank');
    }
  };

  return (
    <div className="file-upload-container">
      <div className="file-upload-label">
        {label} {required && <span className="required-indicator">*</span>}
      </div>

      {
        !viewMode ? (
          <>
            <div className={`file-upload-zone ${isUploading ? 'disabled' : ''}`} style={errors[mediaType] ? { border: '1px solid red' } : {}}>
              <input
                type="file"
                accept={accept}
                multiple={maxFiles > 1}
                onChange={handleFileSelect}
                disabled={isUploading}
              />
              <div className="file-upload-text">
                <u>Browse files</u>
              </div>
            </div>
            {required && <div className="file-upload-help-text">{existingMedia.length >= minFiles && <strong>√</strong>} {existingMedia.length} Uploaded / {minFiles < maxFiles ? 'Minimum' : ''} required: {minFiles}</div>}
            {helpText && <div className="file-upload-help-text">{helpText}</div>}
            {isUploading && <div className="file-upload-progress">Compressing... {uploadProgress}%</div>}
            {error && <div className="file-upload-error">{error}</div>}
            {errors[mediaType] && <div className="file-upload-error">{errors[mediaType]}</div>}
          </>
        ) : null
      }

      {existingMedia.length > 0 && (
        <div className="media-preview">
          {existingMedia.map((media: MediaFile) => (
            <div 
              key={media.id} 
              className="media-item" 
              onClick={() => handleMediaClick(media.signedUrl as unknown as string)}
              onKeyDown={(e) => handleMediaKeyDown(e, media.signedUrl as unknown as string)}
              role="button"
              tabIndex={0}
            >
              {media.contentType?.startsWith('image/') ? (
                <img src={media.signedUrl as unknown as string} alt={media.originalFilename} />
              ) : (
                <div className="media-item-video-placeholder">📹 {media.originalFilename}</div>
              )}
              {!viewMode && <button
                className="media-item-delete"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(media.id);
                }}
                type="button"
              >
                ×
              </button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
